import React from 'react';
import '../styles/ForeignObjectCommon.css';
import './ProjectsForeignObject.css';

interface Project {
    id: string;
    title: string;
    category: string;
    duration: string;
    status: string;
    description: string[];
    technologies: string[];
    highlights?: string[];
    links?: { type: string; url: string }[];
    embedUrl?: string;
}

interface ProjectCategory {
    id: string;
    name: string;
    projects: Project[];
}

interface ProjectsForeignObjectProps {
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    groupName: string;
    displayText: string;
    isHovered: boolean;
    isSelected: boolean;
    clipBounds?: boolean;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
}

// Project data organized by category
const projectCategories: ProjectCategory[] = [
    {
        id: 'web-dev',
        name: 'Web Development',
        projects: [
            {
                id: 'portfolio',
                title: 'Interactive Portfolio Website',
                category: 'Web Development',
                duration: 'Nov 2024 - Present',
                status: 'Active',
                description: [
                    'Built interactive Minecraft-inspired portfolio using React and TypeScript',
                    'Implemented custom SVG rendering system with tile-based architecture',
                    'Designed responsive zoom and pan functionality with smooth animations',
                    'Created modular component system for different content sections'
                ],
                technologies: ['React', 'TypeScript', 'SVG', 'CSS3', 'Responsive Design'],
                highlights: ['Custom Animation System', 'Pixel-Perfect Rendering', 'Mobile Responsive']
            }
        ]
    },
    {
        id: 'gpu-compute',
        name: 'Graphics & GPU Computing',
        projects: [
            {
                id: 'cuda-complex-plane',
                title: 'CUDA + OpenGL Interop: Complex Plane Phase Renderer',
                category: 'GPU Computing',
                duration: '2024',
                status: 'Completed',
                description: [
                    'Implemented CUDA–OpenGL interoperability for real-time complex plane visualization.',
                    'Each pixel represents a point in the complex plane, encoded using its complex phase.',
                    'CUDA kernels compute values massively in parallel, accelerating rendering by orders of magnitude.',
                    'Zero-copy interop: CUDA writes directly into an OpenGL texture buffer (PBO) for display.',
                    'Includes dynamic zoom and parameter adjustments for exploring analytic structures.'
                ],
                technologies: [
                    'CUDA',
                    'OpenGL',
                    'C++',
                    'Parallel Computing',
                    'GPU Programming'
                ],
                highlights: [
                    'Real-Time CUDA Kernels',
                    'Zero-Copy CUDA–OpenGL Interop',
                    'Complex Plane Visualization'
                ],
                links: [
                    { type: 'GitHub', url: 'https://github.com/gametekker/zuu' },
                    { type: 'YouTube Demo', url: 'https://www.youtube.com/watch?v=H7H9GEahH9w' }
                ],
                embedUrl: 'https://www.youtube.com/embed/H7H9GEahH9w'
            }
        ]
    }

];

const ProjectsForeignObject: React.FC<ProjectsForeignObjectProps> = ({ 
    bounds, 
    groupName, 
    displayText, 
    isHovered, 
    isSelected,
    clipBounds = true,
    leftMargin = 0,
    rightMargin = 32,
    topMargin = 16
}) => {

    // Calculate adjusted bounds when clipping is enabled and component is selected
    const getAdjustedBounds = () => {
        if (!isSelected || !clipBounds) {
            return bounds;
        }
        
        return {
            x: bounds.x + leftMargin,
            y: bounds.y + topMargin,
            width: bounds.width - leftMargin - rightMargin,
            height: bounds.height - topMargin
        };
    };

    const adjustedBounds = getAdjustedBounds();

    if (!isSelected) {
        return (
            <div className="foreign-object-base header-only">
                <div className="header-text">{displayText}</div>
            </div>
        );
    }

    return (
        <div 
            className="foreign-object-base expanded"
            style={clipBounds ? {
                width: adjustedBounds.width,
                height: adjustedBounds.height,
                marginLeft: leftMargin,
                marginTop: topMargin,
                marginRight: rightMargin
            } : undefined}
        >
            <div className="header-text">{displayText}</div>
            
            <div className="main-container">
                {projectCategories.map((category) => (
                    <div key={category.id} className="category-section">
                        <div className="section-title project-category-title">{category.name}</div>
                        <div className="section-items">
                            {category.projects.map((project) => (
                                <div key={project.id} className="card-item">
                                    <div className="card-header expanded">
                                        <div className="card-main">
                                            <div className="title-status">
                                                <span className="primary-text">{project.title}</span>
                                                <span className="status status-${project.status.toLowerCase()}">{project.status}</span>
                                            </div>
                                            <div className="duration">{project.duration}</div>
                                        </div>
                                    </div>
                                    
                                    <div className="card-details">
                                        <div className="description">
                                            {project.description.map((item, index) => (
                                                <div key={index} className="description-item">
                                                    • {item}
                                                </div>
                                            ))}
                                        </div>
                                        {project.links && project.links.length > 0 && (
                                            <div className="links-section">
                                                {project.links.map((link, index) => (
                                                    <a 
                                                        key={index}
                                                        href={link.url}
                                                        className="project-link"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {link.type}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                        {project.embedUrl && (
                                            <div className="video-container">
                                                <iframe
                                                    src={project.embedUrl}
                                                    title={`${project.title} video`}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            </div>
                                        )}
                                        
                                        {project.highlights && (
                                            <div className="tag-section">
                                                <div className="tag-label">Key Highlights:</div>
                                                <div className="tag-container">
                                                    {project.highlights.map((highlight, index) => (
                                                        <span key={index} className="tag-base highlight-tag">{highlight}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ height: "4px" }} />
                                        
                                        <div className="tag-section">
                                            <div className="tag-label">Technologies Used:</div>
                                            <div className="tag-container">
                                                {project.technologies.map((tech, index) => (
                                                    <span key={index} className="tag-base project-tech-tag">{tech}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectsForeignObject;